import { PartyPopper, Cake, GraduationCap, Heart, ArrowRight, Phone, CalendarCheck, Sparkles } from "lucide-react";

const EVENT_TYPES = [
  { icon: Heart, label: "Weddings" },
  { icon: PartyPopper, label: "Backyard Parties" },
  { icon: Cake, label: "Birthdays" },
  { icon: GraduationCap, label: "Graduations" },
];

const EVENT_IMG =
  "https://images.pexels.com/photos/2253870/pexels-photo-2253870.jpeg?auto=compress&cs=tinysrgb&w=1400";

export default function EventReady() {
  return (
    <section
      id="event-ready"
      data-testid="event-ready-section"
      className="relative bg-black overflow-hidden border-t border-border"
    >
      {/* Big atmospheric bg image */}
      <div className="absolute inset-0">
        <img
          src={EVENT_IMG}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60" />
        <div
          className="absolute inset-0 mix-blend-screen opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 80% 30%, rgba(127,176,105,0.4), transparent 55%)",
          }}
        />
      </div>

      <div className="container-ic relative py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <div className="overline mb-5" data-testid="event-overline">
              <Sparkles className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
              Event-Ready Yards
            </div>

            <h2
              className="font-display italic text-white text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9]"
              data-testid="event-headline"
            >
              Got a big <br />
              day coming? <br />
              <span className="text-primary">We&apos;ve got</span>{" "}
              <span className="text-primary">your yard.</span>
            </h2>

            <p className="mt-7 text-base md:text-lg text-foreground/90 leading-relaxed max-w-2xl">
              Hosting a wedding, birthday, graduation, or backyard party? Let
              Ironclad bring your outdoor space to life — so your guests step
              into something unforgettable. We handle the cut, the clean, the
              hedges, the beds and the polish so you can focus on the
              <span className="text-primary font-bold"> memories.</span>
            </p>

            {/* Event types */}
            <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {EVENT_TYPES.map((e) => {
                const Icon = e.icon;
                return (
                  <div
                    key={e.label}
                    className="border border-primary/30 bg-black/40 backdrop-blur-sm p-4 hover:border-primary hover:bg-primary/5 transition-colors text-center"
                    data-testid={`event-type-${e.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon className="h-6 w-6 text-primary mx-auto mb-2" />
                    <div className="text-xs uppercase tracking-[0.15em] text-white font-display font-bold">
                      {e.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* What's included strip */}
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-px bg-border border border-border">
              <div className="bg-black/70 backdrop-blur-sm p-5 text-center">
                <CalendarCheck className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="font-display text-sm font-bold text-white uppercase tracking-tight">
                  Pre-Event Polish
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  Scheduled 1–2 days out
                </div>
              </div>
              <div className="bg-black/70 backdrop-blur-sm p-5 text-center">
                <PartyPopper className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="font-display text-sm font-bold text-white uppercase tracking-tight">
                  Photo-Ready Lawn
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  Sharp lines · zero debris
                </div>
              </div>
              <div className="bg-black/70 backdrop-blur-sm p-5 text-center">
                <Sparkles className="h-5 w-5 text-primary mx-auto mb-2" />
                <div className="font-display text-sm font-bold text-white uppercase tracking-tight">
                  Post-Event Cleanup
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  Add-on available
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#booking"
                className="btn-primary group"
                data-testid="event-cta-book"
              >
                Book Your Event Date
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="tel:5129480676"
                className="btn-outline"
                data-testid="event-cta-call"
              >
                <Phone className="h-4 w-4" /> Call 512-948-0676
              </a>
            </div>
            <div className="mt-5 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Book 2+ weeks ahead for priority scheduling · Free on-site walkthrough
            </div>
          </div>

          {/* Right pull-quote / atmosphere card */}
          <div className="lg:col-span-5 hidden lg:flex flex-col gap-5">
            <div className="border-l-2 border-primary pl-6 py-4 bg-black/40 backdrop-blur-sm">
              <div className="overline mb-3">The Ironclad Promise</div>
              <p className="font-display italic text-white text-xl md:text-2xl font-bold tracking-tight leading-snug">
                &ldquo;When your guests arrive, the yard should already be telling
                them <span className="text-primary">it&apos;s going to be a great
                night.</span>&rdquo;
              </p>
              <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-primary">
                — The Ironclad Way
              </div>
            </div>

            <div className="border border-primary/50 bg-primary/8 p-6 backdrop-blur-sm">
              <div className="overline mb-2 text-primary">Hosting Bonus</div>
              <div className="font-display text-2xl md:text-3xl font-black text-white uppercase tracking-tight leading-tight">
                10% Off your <br />
                first event package
              </div>
              <p className="mt-3 text-sm text-foreground/85 leading-relaxed">
                Mention this site when you call. New event clients only —
                limited spots available each weekend.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
